import React, { useState, useEffect } from "react";
import {
  Flex,
  Text,
  Button,
  useToast,
  HStack,
  Badge,
  VStack,
  Divider,
} from "@chakra-ui/react";
import { requestAccount } from "../../services/requestAccount";
import { color } from "../../utils/theme";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/reducer/rootReducer";
import { getUserById } from "../../services/getUserById";
import { setUser } from "../../redux/action/userAction";
import { User } from "../../utils/types/user";
import ApprovalRequestModal from "./approvalRequestModal";
import UserApprovalModal from "./userApprovalModal";

export default function TrialMode({
  createdAt,
  userId,
  isAccountRequested,
}: {
  createdAt: string;
  userId: number;
  isAccountRequested: boolean;
}) {
  const [countdown, setCountdown] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [isRequestModalOpen, setIsRequestMOdalOpen] = useState(false);
  const [isUserRequestModalOpen, setIsUserRequestModalOpen] = useState(false);

  const user = useSelector((state: RootState) => state.user);
  const toast = useToast();
  const dispatch = useDispatch();
  useEffect(() => {
    const accountCreationDate = new Date(createdAt).getTime();
    const currentDate = new Date().getTime();
    const elapsedTime = Math.floor((currentDate - accountCreationDate) / 1000);
    const initialCountdown = 30 * 24 * 60 * 60 - elapsedTime;

    setCountdown(initialCountdown > 0 ? initialCountdown : 0);

    const interval = setInterval(() => {
      setCountdown((prevCountdown) =>
        prevCountdown > 0 ? prevCountdown - 1 : 0
      );
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [createdAt]);

  useEffect(() => {
    const fetchData = async () => {
      await getUserById(userId).then((res) =>
        dispatch(setUser(res as User) as any)
      );
    };
    fetchData();
  }, [isLoading]);

  const formatTime = (time: number) => {
    const days = Math.floor(time / (24 * 60 * 60));
    const hours = Math.floor((time % (24 * 60 * 60)) / (60 * 60));
    const minutes = Math.floor((time % (60 * 60)) / 60);
    const seconds = Math.floor(time % 60);

    return `${days} days ${hours} hours ${minutes} minutes ${seconds} seconds`;
  };

  const getTrialModeTitle = (status: string | undefined) => {
    let toDisplayTitle = null;
    switch (status) {
      case "Pending":
        toDisplayTitle = `You are currently in trial mode, and your data will not be saved once
        the trial period concludes.`;
        break;
      case "Approved":
        toDisplayTitle = ` `;
        break;
      case "Rejected":
        toDisplayTitle = `Your approval request has been rejected.`;
        break;
      default:
        toDisplayTitle = `You are currently in trial mode, and your data will not be saved once
        the trial period concludes.`;
    }
    return toDisplayTitle;
  };
  const requestUserApproval = (file: any) => {
    setIsLoading(true);
    requestAccount(userId, file)
      .then((res) => {
        setIsLoading(false);

        toast({
          title: "Account Requested Successfully",
          position: "top-right",
          description: "Account request submitted!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      })
      .catch((error) => {
        setIsLoading(false);
        toast({
          title: "Account Request failed",
          position: "top-right",
          description: "Please try again!",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      });
  };

  return (
    <>
      <ApprovalRequestModal
        isOpen={isRequestModalOpen}
        onClose={() => {
          setIsRequestMOdalOpen(false);
        }}
        request={user.user?.approval_requests[0]}
        submitRequest={(file: any) => {
          requestUserApproval(file);
        }}
      />
      <UserApprovalModal
        isOpen={isUserRequestModalOpen}
        onClose={() => {
          setIsUserRequestModalOpen(false);
        }}
        submitRequest={(file: any) => {
          requestUserApproval(file);
        }}
      />

      <Flex
        direction={"row"}
        alignContent={"center"}
        alignItems={"center"}
        h={"120px"}
        w={"100%"}
        px={10}
        justifyContent={"space-between"}
        bgGradient={
          user.user?.approval_requests[0]?.status === "Rejected"
            ? "linear(to-l, #FF6347  ,#FF0000)"
            : "linear(to-l, #7D5AD3, #1985A2)"
        }
      >
        <Text
          color={color.secondary}
          fontWeight={"bold"}
          fontSize={14}
          fontFamily={"sans-serif"}
          textAlign={"left"}
        >
          {getTrialModeTitle(user.user?.approval_requests[0]?.status)}
          <Text fontSize={14}> Time remaining: {formatTime(countdown)}</Text>
          <Divider my={2} />

          <HStack>
            {user?.user?.approval_requests?.length ? (
              <>
                <Text
                  fontSize="md"
                  fontWeight="bold"
                  color={"white"}
                  display={"flex"}
                  flexDirection={"column"}
                  justifyContent={"flex-start"}
                  alignItems={"flex-start"}
                  alignContent={"flex-start"}
                  textAlign={"left"}
                >
                  Status
                </Text>
                <Badge
                  ml="1"
                  fontSize="0.8em"
                  colorScheme={
                    user.user?.approval_requests[0].status === "Pending"
                      ? "green"
                      : user.user?.approval_requests[0].status === "Verified"
                      ? "blue"
                      : "red"
                  }
                  p={1}
                >
                  {user.user?.approval_requests[0].status === "Pending"
                    ? "Waiting Approval"
                    : user.user?.approval_requests[0].status === "Verified"
                    ? "Verified"
                    : "Rejected"}
                </Badge>
              </>
            ) : (
              <Badge ml="1" fontSize="0.8em" colorScheme="gray" p={1}>
                No Request
              </Badge>
            )}
          </HStack>
        </Text>
        <HStack>
          <Divider orientation="vertical" w={2} color={"gray"} />
          {user.user?.approval_requests[0]?.status !== "Rejected" &&
          user.user?.approval_requests[0]?.status !== "Verified" &&
            user.user?.approval_requests[0]?.status !== "Pending" && (
              <Button
                colorScheme={"blue"}
                p={4}
                _disabled={{ zIndex: 20 }}
                isLoading={isLoading}
                onClick={() => {
                  setIsUserRequestModalOpen(true);
                }}
              >
                {/* {user.user?.approval_requests[0]?.status === "Pending"
                ? "Waiting approval" */}
                Request an account
              </Button>
            )}
          {user.user?.approval_requests[0] && (
            <Button
              colorScheme={
                user.user?.approval_requests[0]?.status === "Pending"
                  ? "green"
                  : "blue"
              }
              p={4}
              _disabled={{ zIndex: 20 }}
              onClick={() => {
                setIsRequestMOdalOpen(true);
              }}
            >
              Account Request Info.
            </Button>
          )}
        </HStack>
      </Flex>
    </>
  );
}
